// Aignoz — sticky cross-page breadcrumb sub-nav (Phase 4).
// Renders below the chunky sidebar/header. Active page highlighted in magenta-pink.
// Phase 5: adds a right-anchored UserChip with persona switch + logout.
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Factory, Atom, Compass, Briefcase, Cog, Sparkles, LogOut, ChevronDown, Rocket, TrendingUp, Microscope, Stethoscope, Building2, ShieldCheck, User, UserCog, PieChart, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/auth-context";

const MAGENTA = "#D17EC0";

const LINKS = [
  { key: "landing",  to: "/",         label: "Aignoz Idea Factory", icon: Sparkles, brand: true },
  { key: "matrix",   to: "/matrix",   label: "Matrix",              icon: Atom },
  { key: "sources",  to: "/sources",  label: "10 Sources",          icon: Compass },
  { key: "factory",  to: "/factory",  label: "Factory",             icon: Factory },
  { key: "captable", to: "/captable", label: "Cap Table",           icon: PieChart },
  { key: "workspace",to: "/workspace",label: "Workspace",           icon: Briefcase, hideOnMobile: true },
  { key: "templates",to: "/templates",label: "Templates",           icon: FileText, hideOnMobile: true },
  { key: "pricing",  to: "/pricing",  label: "Pricing",             icon: Cog, hideOnMobile: true },
];

const PERSONA_OPTIONS = [
  { key: "founder",          label: "Founder",          icon: Rocket },
  { key: "investor",         label: "Investor",         icon: TrendingUp },
  { key: "researcher",       label: "Researcher",       icon: Microscope },
  { key: "clinician",        label: "Clinician",        icon: Stethoscope },
  { key: "incubator",        label: "Incubator",        icon: Building2 },
  { key: "enterprise_admin", label: "Enterprise Admin", icon: ShieldCheck },
];

function personaMeta(key) {
  return PERSONA_OPTIONS.find((p) => p.key === key) || PERSONA_OPTIONS[0];
}

export default function BreadcrumbNav() {
  const loc = useLocation();
  const path = loc.pathname.replace(/\/$/, "") || "/";
  const isActive = (to) => {
    if (to === "/") return path === "/";
    return path === to || path.startsWith(to + "/");
  };
  return (
    <nav
      data-testid="breadcrumb-nav"
      className="sticky top-0 z-30 backdrop-blur-md border-b border-white/[0.06]"
      style={{ background: "rgba(11,12,16,0.78)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 h-9 flex items-center gap-1 overflow-x-auto">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {LINKS.map((L, i) => {
            const active = isActive(L.to);
            return (
              <div key={L.key} className="flex items-center gap-1 shrink-0">
                {i > 0 && (
                  <span className="text-white/20 mx-0.5 text-[10px] select-none" aria-hidden>·</span>
                )}
                <Link
                  to={L.to}
                  data-testid={`breadcrumb-link-${L.key}`}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition ${
                    active ? "font-medium" : "text-white/55 hover:text-white"
                  } ${L.hideOnMobile ? "hidden md:inline-flex" : ""}`}
                  style={active ? {
                    color: L.brand ? MAGENTA : "white",
                    background: "rgba(209,126,192,0.10)",
                    border: "1px solid rgba(209,126,192,0.35)",
                  } : { border: "1px solid transparent" }}
                >
                  <L.icon className="w-3 h-3" />
                  <span className={L.brand ? "tracking-tight" : "uppercase tracking-[0.12em]"}>
                    {L.label}
                  </span>
                </Link>
              </div>
            );
          })}
        </div>
        <UserChip />
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// UserChip — persona badge + dropdown (switch persona / logout)
// ---------------------------------------------------------------------------
function UserChip() {
  const { user, logout, updatePersona } = useAuth();
  const navigate = useNavigate();
  const [switchOpen, setSwitchOpen] = useState(false);
  if (!user) return null;
  const meta = personaMeta(user.persona);
  const Icon = meta.icon;

  async function handleLogout() {
    await logout();
    toast("Signed out");
    navigate("/login", { replace: true });
  }

  async function pickPersona(key) {
    if (key === user.persona) { setSwitchOpen(false); return; }
    const r = await updatePersona(key);
    if (r.ok) {
      toast.success(`Persona → ${personaMeta(key).label}`);
    } else {
      toast.error("Could not update persona", { description: r.error });
    }
    setSwitchOpen(false);
  }

  return (
    <div className="shrink-0 flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-chip"
            className="inline-flex items-center gap-1.5 pl-1.5 pr-2 py-1 rounded-md border border-white/[0.10] hover:border-white/[0.20] text-[11px] text-white/85"
          >
            <span
              className="w-5 h-5 rounded-full inline-flex items-center justify-center"
              style={{ background: `${MAGENTA}22`, border: `1px solid ${MAGENTA}66`, color: MAGENTA }}
              data-testid="user-chip-persona"
            >
              <Icon className="w-3 h-3" />
            </span>
            <span className="hidden sm:inline">
              <span className="text-white/50 uppercase tracking-[0.12em] text-[9px] mr-1">{meta.label}</span>
              <span className="text-white/90">{user.name || user.email.split("@")[0]}</span>
            </span>
            <ChevronDown className="w-3 h-3 text-white/40" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5 text-[10px] mono text-white/45 truncate">{user.email}</div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setSwitchOpen(true)}
            data-testid="user-chip-switch-persona"
            className="gap-2 text-[12px]"
          >
            <UserCog className="w-3.5 h-3.5" /> Switch persona
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleLogout}
            data-testid="user-chip-logout"
            className="gap-2 text-[12px] text-rose-300 focus:text-rose-200"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={switchOpen} onOpenChange={setSwitchOpen}>
        <DialogContent className="max-w-md" data-testid="persona-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-4 h-4" /> Switch persona
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-white/55 italic">
            The OS adapts to the lens you pick. Change any time.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {PERSONA_OPTIONS.map((p) => {
              const isSel = p.key === user.persona;
              return (
                <button
                  key={p.key}
                  onClick={() => pickPersona(p.key)}
                  data-testid={`persona-modal-${p.key}`}
                  className={`rounded-lg border px-3 py-2.5 text-left transition ${
                    isSel
                      ? "border-[rgba(209,126,192,0.65)] bg-[rgba(209,126,192,0.10)]"
                      : "border-white/[0.10] bg-white/[0.02] hover:border-white/[0.20]"
                  }`}
                  style={isSel ? { color: MAGENTA } : {}}
                >
                  <div className="flex items-center gap-1.5">
                    <p.icon className="w-3.5 h-3.5" />
                    <span className="text-[12px] font-medium">{p.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
